/**
 * Risk #1 / #2 hit panels — CANON: fill RiskHitPanelLayout slots only.
 * Do not add a custom card shell. See `./riskCardLayoutCanon.js`.
 */
import { Link } from 'react-router-dom';
import RiskHitPanelLayout, {
  RISK_HIT_LAYOUT,
  RiskHitMetricGrid,
} from './RiskHitPanelLayout';
import TransmissionScalePlaceholder from './TransmissionScalePlaceholder';
import TransmissionScheduleTimeline from './TransmissionScheduleTimeline';
import { getRiskLevelShortLabel } from '../constants/riskTypes';
import { resolveSupportActionLabel } from '../constants/supportActionLabels';
import { buildDeviceHistoryPath } from '../pages/deviceHistoryPath';
import { RISK_LABELS, riskBarStyle, riskScoreStyle } from './riskScoreStyles';

export { RISK_LABELS, riskBarStyle, riskScoreStyle };

const SCHEDULE_BADGE_STYLES = {
  День: 'border-sky-400/30 bg-sky-400/10 text-sky-700 dark:text-sky-200',
  Неделя: 'border-violet-400/30 bg-violet-400/10 text-violet-700 dark:text-violet-200',
  Месяц: 'border-amber-400/30 bg-amber-400/10 text-amber-800 dark:text-amber-200',
};

const RISK_METRIC_HINTS = {
  overdue: 'С момента первого пропущенного слота по графику (день, неделя или месяц).',
  offline: 'С момента последней реальной связи контроллера.',
  missedSlots: 'Сколько ожидаемых выходов на связь пропущено с начала просрочки.',
  missedTransmissionWindows:
    'Сколько наступивших моментов передачи из настройки интеграции пропущено с начала сбоя связи (пока устройство всё ещё overdue).',
};

function RiskCardMetrics({ rows }) {
  return <RiskHitMetricGrid rows={rows} />;
}

function formatIntegrationKeys(facts) {
  const names = facts.active_integration_names?.filter(Boolean) ?? [];
  if (names.length > 0) {
    return names.join(', ');
  }
  const keys = facts.active_integration_keys?.filter(Boolean) ?? [];
  return keys.length > 0 ? keys.join(', ') : 'нет';
}

export function formatDateTime(value) {
  if (!value) {
    return 'нет данных';
  }

  return new Intl.DateTimeFormat('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value));
}

function formatOverdueDuration(minutes) {
  const totalMinutes = Math.max(0, Math.floor(Number(minutes) || 0));

  if (totalMinutes < 60) {
    return `${totalMinutes} мин.`;
  }

  if (totalMinutes < 24 * 60) {
    const hours = Math.round((totalMinutes / 60) * 10) / 10;
    return Number.isInteger(hours) ? `${hours} ч.` : `${hours.toFixed(1)} ч.`;
  }

  const days = Math.round((totalMinutes / (24 * 60)) * 10) / 10;
  return `${days.toFixed(1)} дн.`;
}

function formatAttachedCounters(counters) {
  if (!counters?.length) {
    return null;
  }

  return counters
    .map((counter) => {
      const serial = counter.serial_number || 'без serial';
      return counter.input_number != null ? `${serial} (вход ${counter.input_number})` : serial;
    })
    .join(', ');
}

export default function RiskCard({
  item,
  onOpenClientLk,
  openingUserId,
  onOpenHistory,
  hideFio = false,
  hideContactsInAccordion = false,
  hideLk = false,
  embedded = false,
}) {
  const { facts, features, rule_result: ruleResult } = item;
  const riskScore =
    ruleResult.risk_score == null || !Number.isFinite(Number(ruleResult.risk_score))
      ? null
      : Number(ruleResult.risk_score);
  const riskLevel = ruleResult.risk_level ?? (riskScore == null ? 'unknown' : ruleResult.risk_level);
  const frequencyLabel = features.schedule_frequency_label || 'День';
  const frequencyStyle = SCHEDULE_BADGE_STYLES[frequencyLabel] || SCHEDULE_BADGE_STYLES.День;
  const showOfflineDays = features.offline_days * 24 * 60 > (features.minutes_overdue ?? 0);

  const criticalRows = [];
  const detailRows = [];

  if (!hideFio) {
    criticalRows.push({
      key: 'user',
      label: 'ФИО',
      value: facts.user_full_name || <span className="font-normal text-slate-500">не указано</span>,
    });
  }

  criticalRows.push(
    {
      key: 'action',
      label: 'Действие',
      value: resolveSupportActionLabel(ruleResult),
    },
    {
      key: 'overdue',
      label: 'Просрочка',
      hint: RISK_METRIC_HINTS.overdue,
      value: formatOverdueDuration(features.minutes_overdue ?? features.days_overdue * 24 * 60),
      numeric: true,
    },
  );

  if (showOfflineDays) {
    criticalRows.push({
      key: 'offline',
      label: 'Без связи',
      hint: RISK_METRIC_HINTS.offline,
      value: `${features.offline_days} дн.`,
      numeric: true,
    });
  }

  criticalRows.push(
    {
      key: 'missed-slots',
      label: 'Пропущено выходов',
      hint: RISK_METRIC_HINTS.missedSlots,
      value: features.missed_connection_slots ?? 0,
      numeric: true,
    },
    {
      key: 'missed-transmission',
      label: 'Пропущено передач',
      hint: RISK_METRIC_HINTS.missedTransmissionWindows,
      value: features.missed_transmission_periods ?? features.missed_transmission_windows ?? 0,
      numeric: true,
    },
  );

  detailRows.push({
    key: 'integration',
    label: 'Интеграция',
    value: features.has_active_integration ? formatIntegrationKeys(facts) : 'нет',
  });

  if (facts.activated_at) {
    detailRows.push({
      key: 'activated',
      label: 'Активация',
      value: formatDateTime(facts.activated_at),
    });
  }

  if (facts.last_voltage_v != null) {
    detailRows.push({
      key: 'voltage',
      label: 'Напряжение',
      value: `${facts.last_voltage_v} В`,
      numeric: true,
    });
  }

  if (facts.last_signal_dbm != null && !facts.signal_is_unknown) {
    detailRows.push({
      key: 'signal',
      label: 'Сигнал',
      value: facts.signal_is_weak
        ? `${facts.last_signal_dbm} дБм · слабый`
        : `${facts.last_signal_dbm} дБм`,
      numeric: true,
    });
  }

  if (facts.connection_stage_label) {
    detailRows.push({
      key: 'stage',
      label: 'Стадия связи',
      value: facts.connection_stage_label,
    });
  }

  const hasScheduleAnchor =
    features.send_at_day != null || features.transmission_instant || features.first_transmission_at;
  const showTimeline =
    hasScheduleAnchor &&
    (features.expected_connection_time || features.failure_at || features.last_connection_time);

  return (
    <RiskHitPanelLayout
      embedded={embedded}
      riskNumber={1}
      badges={
        <>
          <span className="inline-flex shrink-0 rounded-full border border-slate-300 dark:border-slate-600/50 bg-slate-100 dark:bg-slate-800/80 px-2 py-0.5 text-xs font-medium text-slate-700 dark:text-slate-300">
            Контроллер
          </span>
          {facts.controller_connection_type_label && (
            <span className="inline-flex shrink-0 rounded-full border border-cyan-400/30 bg-cyan-400/10 px-2 py-0.5 text-xs font-medium text-cyan-700 dark:text-cyan-200">
              {facts.controller_connection_type_label}
            </span>
          )}
          <span className={`inline-flex shrink-0 rounded-full border px-2 py-0.5 text-xs font-medium ${frequencyStyle}`}>
            {frequencyLabel}
          </span>
          {facts.firmware_version ? (
            <span className="inline-flex shrink-0 rounded-full border border-emerald-400/30 bg-emerald-400/10 px-2 py-0.5 text-xs font-medium text-emerald-700 dark:text-emerald-200">
              v{facts.firmware_version}
            </span>
          ) : (
            <span className="inline-flex shrink-0 rounded-full border border-slate-300 dark:border-slate-700 bg-slate-100 dark:bg-slate-900/80 px-2 py-0.5 text-xs font-medium text-slate-500">
              Прошивка: не указана
            </span>
          )}
          {facts.signal_is_weak && (
            <span className="inline-flex shrink-0 rounded-full border border-amber-400/40 bg-amber-400/10 px-2 py-0.5 text-xs font-medium text-amber-800 dark:text-amber-200">
              Слабый сигнал
            </span>
          )}
          {facts.hot_water_unused && (
            <span className="inline-flex shrink-0 rounded-full border border-sky-400/40 bg-sky-400/10 px-2 py-0.5 text-xs font-medium text-sky-800 dark:text-sky-200">
              ГВС не использует
            </span>
          )}
        </>
      }
      title={facts.serial_number || 'Без serial'}
      scoreDisplay={riskScore == null ? '—' : riskScore.toFixed(1)}
      riskLevel={riskLevel}
      scoreBarValue={riskScore ?? 0}
      feature={
        showTimeline ? (
          <TransmissionScheduleTimeline
            failureAt={
              features.expected_connection_time || features.failure_at || features.last_connection_time
            }
            transmissionAt={features.transmission_instant || features.first_transmission_at}
            scheduleLabel={
              features.transmission_schedule_label ||
              (features.send_at_day != null ? `${features.send_at_day}-е число месяца (MSK)` : null)
            }
            missedPeriods={features.missed_transmission_periods ?? features.missed_transmission_windows}
            sendAtDay={features.send_at_day}
            sendAtHour={features.send_at_hour}
          />
        ) : (
          <TransmissionScalePlaceholder features={features} facts={facts} hasScheduleAnchor={hasScheduleAnchor} />
        )
      }
      metrics={<RiskCardMetrics rows={criticalRows} />}
      details={
        <>
          {detailRows.length > 0 && <RiskCardMetrics rows={detailRows} />}
          <div className="space-y-2 text-sm">
            <p>
              <span className="text-slate-500">Частота:</span> {frequencyLabel}
              {features.schedule_detail_label ? ` · ${features.schedule_detail_label}` : ''}
            </p>
            <p>
              <span className="text-slate-500">График:</span> {features.schedule_label}
            </p>
            <p>
              <span className="text-slate-500">Последняя связь:</span>{' '}
              {formatDateTime(features.last_connection_time)}
            </p>
            <p>
              <span className="text-slate-500">Дедлайн:</span> {formatDateTime(ruleResult.deadline_at)}
            </p>
            {!hideContactsInAccordion && facts.user_phone && (
              <p>
                <span className="text-slate-500">Телефон:</span> {facts.user_phone}
              </p>
            )}
            {!hideContactsInAccordion && facts.address && (
              <p>
                <span className="text-slate-500">Адрес:</span> {facts.address}
              </p>
            )}
            {facts.attached_counters?.length > 0 && (
              <p>
                <span className="text-slate-500">Счётчики:</span>{' '}
                {formatAttachedCounters(facts.attached_counters)}
              </p>
            )}
          </div>
          {ruleResult.reason_codes?.length > 0 && (
            <div>
              <p className="mb-1.5 text-xs text-slate-500">Причины решения</p>
              <div className="flex flex-wrap gap-1.5">
                {ruleResult.reason_codes.map((code) => (
                  <span
                    key={code}
                    className="rounded-full bg-white px-2 py-1 text-xs text-slate-500 dark:bg-slate-900 dark:text-slate-400"
                  >
                    {code}
                  </span>
                ))}
              </div>
            </div>
          )}
        </>
      }
      actions={
        <>
          {facts.device_id != null && onOpenHistory && (
            <button
              type="button"
              onClick={() => onOpenHistory(facts)}
              className={`${RISK_HIT_LAYOUT.actionBtn} ${RISK_HIT_LAYOUT.actionHistory}`}
            >
              История рисков
            </button>
          )}
          {facts.device_id != null && (
            <Link
              to={buildDeviceHistoryPath(facts.device_id, { serialNumber: facts.serial_number })}
              className={`${RISK_HIT_LAYOUT.actionBtn} ${RISK_HIT_LAYOUT.actionSecondary}`}
            >
              История устройства
            </Link>
          )}
          {!hideLk && facts.user_id && facts.lk_client_available === true && onOpenClientLk && (
            <button
              type="button"
              onClick={() => onOpenClientLk(facts.user_id)}
              disabled={openingUserId === facts.user_id}
              className={`${RISK_HIT_LAYOUT.actionBtn} ${RISK_HIT_LAYOUT.actionLk}`}
            >
              {openingUserId === facts.user_id ? 'Открываем ЛК...' : 'ЛК клиента'}
            </button>
          )}
        </>
      }
      footerNote={
        !hideLk && facts.serial_number && facts.lk_client_available === false ? (
          <p className="rounded-xl border border-amber-400/20 bg-amber-400/5 px-3 py-2 text-sm text-amber-800 dark:text-amber-200/90">
            ЛК клиента: пользователь не привязан
          </p>
        ) : null
      }
    />
  );
}